import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { patientService } from '../../services/patientService';
import SidebarNavigation from '../../components/navigation/SidebarNavigation';
import { psychologistService } from '../../services/psychologistService';
import PatientTableRow from './components/PatientTableRow';
import PatientCard from './components/PatientCard';
import PatientFormModal from './components/PatientFormModal';
import PatientDetailModal from './components/PatientDetailModal';
import DeleteConfirmationModal from './components/DeleteConfirmationModal';
import EmptyState from './components/EmptyState';
import { List, Loader2, AlertCircle, Users, Plus } from 'lucide-react';

import AppointmentModal from '../schedule-dashboard/components/AppointmentModal';
import SuccessDialog from '../schedule-dashboard/components/SuccessDialog';

export default function PatientManagement() {
  const { userProfile } = useAuth();
  const [patients, setPatients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [viewMode, setViewMode] = useState('table');
  const [showAddModal, setShowAddModal] = useState(false);
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [patientToDelete, setPatientToDelete] = useState(null);
  const [showAppointmentModal, setShowAppointmentModal] = useState(false);
  const [patientForAppointment, setPatientForAppointment] = useState(null);
  const [showSuccessDialog, setShowSuccessDialog] = useState(false);
  const [showDeleteSuccessDialog, setShowDeleteSuccessDialog] = useState(false);
  const [showCreateSuccessDialog, setShowCreateSuccessDialog] = useState(false);
  const [psychologistProfile, setPsychologistProfile] = useState(null);  
  const [filters, setFilters] = useState({
    search: '',
    status: 'all'
  });

  useEffect(() => {
    if (userProfile?.id) {
      loadPsychologistProfile();
      loadPatients();
    }
  }, [userProfile?.id, filters]);

  const loadPatients = async () => {
    if (!userProfile?.id) return;

    setLoading(true);
    setError('');

    try {
      const filterParams = {
        status: filters?.status !== 'all' ? filters?.status : undefined,
        search: filters?.search || undefined
      };

      const { data, error: fetchError } = await patientService?.getPatients(
        userProfile?.id,
        filterParams
      );

      if (fetchError) throw fetchError;
      setPatients(data || []);
    } catch (err) {
      console.error('Error loading patients:', err);
      setError(err?.message || 'Erro ao carregar pacientes');
    } finally {
      setLoading(false);
    }
  };

  const loadPsychologistProfile = async () => {
    if (!userProfile?.id) return;

    try {
      const { data, error: profileError } = await psychologistService?.getPsychologistById(userProfile?.id);
      if (profileError) throw profileError;
      
      // Handle case where no psychologist profile exists yet
      if (!data) {
        //console.log('No psychologist profile found for user');
        setPsychologistProfile(null);
        return;
      }
      setPsychologistProfile(data);
    } catch (err) {
      console.error('Error loading psychologist profile:', err);
      // Don't set error state - just log it, as this is not critical for the schedule view
    }
  };

  const handleAddPatient = async (patientData) => {
    try {
      const { data, error: createError } = await patientService?.createPatient({
        ...patientData,
        psychologist_id: userProfile?.id
      });

      if (createError) throw createError;

      setPatients(prev => [data, ...prev]);
      setShowAddModal(false);
      setShowCreateSuccessDialog(true);
      return { success: true };
    } catch (err) {
      console.error('Error creating patient:', err);
      return { success: false, error: err?.message };
    }
  };

  const handleEditPatient = async (id, updates) => {
    try {
      const { data, error: updateError } = await patientService?.updatePatient(id, updates);

      if (updateError) throw updateError;

      setPatients(prev => prev?.map(p => p?.id === id ? data : p));
      setSelectedPatient(null);
      setShowSuccessDialog(true);
      return { success: true };
    } catch (err) {
      console.error('Error updating patient:', err);
      return { success: false, error: err?.message };
    }
  };

  const handleDeletePatient = async () => {
    if (!patientToDelete) return;

    try {
      const { error: deleteError } = await patientService?.deletePatient(patientToDelete?.id);

      if (deleteError) throw deleteError;

      setPatients(prev => prev?.filter(p => p?.id !== patientToDelete?.id));
      setShowDeleteModal(false);
      setPatientToDelete(null);
      setShowDeleteSuccessDialog(true);
    } catch (err) {
      console.error('Error deleting patient:', err);
      alert(`Erro ao excluir paciente: ${err?.message}`);
    }
  };

  const handleViewDetails = (patient) => {
    setSelectedPatient(patient);
    setShowDetailModal(true);
  };

  const handleEditClick = (patient) => {
    setSelectedPatient(patient);
    setShowDetailModal(false);
  };

  const handleScheduleAppointment = (patient) => {
    setPatientForAppointment(patient);
    setShowAppointmentModal(true);
  };

  const handleSaveAppointment = async (appointmentData) => {
    try {
      const { appointmentService } = await import('../../services/appointmentService');
      
      const { data, error: createError } = await appointmentService?.createAppointment({
        ...appointmentData,
        psychologist_id: userProfile?.id
      });

      if (createError) throw createError;

      setShowAppointmentModal(false);
      setPatientForAppointment(null);
      
      // Show success message
      alert('Agendamento criado com sucesso!');
      
      return { success: true };
    } catch (err) {
      console.error('Error creating appointment:', err);
      return { success: false, error: err?.message };
    }
  };

  const handleDeleteClick = (patient) => {
    setPatientToDelete(patient);
    setShowDeleteModal(true);
  };

  const filteredPatients = patients;

  return (
    <div className="flex min-h-screen" style={{ backgroundColor: '#d3d3d3' }}>
      <SidebarNavigation />
      <main className="flex-1 lg:ml-64">
        <div className="p-3 sm:p-4 lg:p-4 pt-4 sm:pt-4 lg:pt-6 lg:mt-0 max-w-full overflow-x-hidden">
          {/* Header - Standardized Format */}
          <div className="mb-8">
            <div className="bg-gray-100 rounded-xl shadow-lg shadow-md p-6 max-w-6xl">

              {/* Desktop */}
              <div className="hidden md:flex items-center gap-3 mb-2 flex-nowrap">
                <Users className="w-8 h-8 text-[#969696]" />

                <h1 className="text-3xl font-bold text-[#969696] font-manrope whitespace-nowrap">
                  Meus Pacientes
                </h1>
              </div>

              {/* Mobile */}
              <div className="md:hidden flex items-center gap-2 mb-1">
                <Users className="w-8 h-8 text-[#969696]" />

                <h1 className="text-2xl font-bold text-[#969696] font-manrope">
                  Meus Pacientes
                </h1>
              </div>

              {/* Desktop - Mostra subtítulo */}
              <p className="hidden md:block text-[#969696] font-manrope text-lg">
                Gerencie e acompanhe pacientes
              </p>

            </div>
          </div>

          {/* Loading State */}
          {loading && (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
            </div>
          )}

          {/* Error State */}
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
              <div className="flex items-center gap-2">
                <AlertCircle className="w-5 h-5 text-red-600" />
                <p className="text-sm text-red-800">{error}</p>
              </div>
            </div>
          )}

          {/* Content - Patient List */}
          {!loading && !error && (
            <>
              {filteredPatients?.length === 0 ? (
                <EmptyState onAddPatient={() => setShowAddModal(true)} />
              ) : (
                <>
                  {viewMode === 'table' ? (
                    <div className="bg-white rounded-lg shadow-sm border border-gray-200 w-full">
                      <div className="w-full max-h-[calc(110vh-14rem)] overflow-y-auto">
                        <table className="w-full min-w-full">
                          <thead className="bg-gray-200 border-b border-gray-200 sticky top-0 z-10">
                            <tr>
                              <th className="px-3 sm:px-4 lg:px-6 py-2 sm:py-3 text-left text-xs sm:text-sm font-medium text-gray-700">
                                Paciente
                              </th>
                              <th className="px-3 sm:px-4 lg:px-6 py-2 sm:py-3 text-left text-xs sm:text-sm font-medium text-gray-700 hidden sm:table-cell">
                                WhatsApp
                              </th>
                              <th className="px-3 sm:px-4 lg:px-6 py-2 sm:py-3 text-center text-xs sm:text-sm font-medium text-gray-700 hidden md:table-cell">
                                Sessões
                              </th>
                              <th className="px-3 sm:px-4 lg:px-6 py-2 sm:py-3 text-center text-xs sm:text-sm font-medium text-gray-700 hidden md:table-cell">
                                Última visita
                              </th>
                              <th className="px-3 sm:px-4 lg:px-6 py-2 sm:py-3 text-center text-xs sm:text-sm font-medium text-gray-700">
                                Ações
                              </th>
                            </tr>
                          </thead>
                          <tbody className="bg-white divide-y divide-gray-200">
                            {filteredPatients?.map((patient, index) => (
                              <PatientTableRow
                                key={patient?.id}
                                patient={patient}
                                index={index}
                                onView={handleViewDetails}
                                onViewDetails={handleViewDetails}
                                onEdit={handleEditClick}
                                onDelete={handleDeleteClick}
                                onSchedule={() => {}}
                              />
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                      {filteredPatients?.map((patient) => (
                        <PatientCard
                          key={patient?.id}
                          patient={patient}
                          onView={handleViewDetails}
                          onViewDetails={handleViewDetails}
                          onEdit={handleEditClick}
                          onDelete={handleDeleteClick}
                          onSchedule={() => {}}
                        />
                      ))}
                    </div>
                  )}
                </>
              )}
            </>
          )}
        </div>
      </main>

      {/* FIXED: Floating Action Button (FAB) for "Nova Consulta" - Responsive positioning */}
      <button
        onClick={() => {
          setShowAddModal(true);
        }}
        className="fixed bottom-6 right-6 lg:top-6 lg:bottom-auto z-50 flex items-center justify-center gap-2 bg-blue-600 hover:bg-indigo-700 text-white rounded-full shadow-lg hover:shadow-xl transition-all duration-200 active:scale-95 p-4 lg:px-6 lg:py-4"
        title="Novo Paciente"
        aria-label="Criar novo paciente"
      >
        <Plus className="w-6 h-6" />
        <span className="hidden lg:inline-block font-medium">Novo Paciente</span>
      </button>


      {/* Modals */}
      {showAddModal && (
        <PatientFormModal
          tempPatient={false}
          isOpen={showAddModal}
          onClose={() => setShowAddModal(false)}
          onSave={handleAddPatient}
        />
      )}
      {selectedPatient && !showDetailModal && (
        <PatientFormModal
          tempPatient={false}
          isOpen={true}
          patient={selectedPatient}
          onClose={() => setSelectedPatient(null)}
          onSave={(updates) => handleEditPatient(selectedPatient?.id, updates)}
        />
      )}
      {showDetailModal && selectedPatient && (
        <PatientDetailModal
          isOpen={showDetailModal}
          patient={selectedPatient}
          onClose={() => {
            setShowDetailModal(false);
            setSelectedPatient(null);
          }}
          onEdit={handleEditClick}
          onDelete={() => {
            setShowDetailModal(false);
            handleDeleteClick(selectedPatient);
          }}
          onSchedule={handleScheduleAppointment}
        />
      )}
      {showDeleteModal && patientToDelete && (
        <DeleteConfirmationModal
          isOpen={showDeleteModal}
          patient={patientToDelete}
          patientName={patientToDelete?.name || ''}
          onConfirm={handleDeletePatient}
          onCancel={() => {
            setShowDeleteModal(false);
            setPatientToDelete(null);
          }}
          onClose={() => {
            setShowDeleteModal(false);
            setPatientToDelete(null);
          }}
          isDeleting={false}
        />
      )}
      
      {/* Appointment Modal */}
      {showAppointmentModal && patientForAppointment && (
        <AppointmentModal
          isOpen={showAppointmentModal}
          patients={patients}
          patientsLoading={false}
          selectedDate={new Date()}
          selectedTime={null}
          editingAppointment={null}
          fromPatientManagement={true}
          preSelectedPatientId={patientForAppointment?.id}
          psychologistProfile={psychologistProfile}
          onClose={() => {
            setShowAppointmentModal(false);
            setPatientForAppointment(null);
          }}
          onSave={handleSaveAppointment}
        />
      )}

      {/* Success Dialog for Update */}
      <SuccessDialog
        isOpen={showSuccessDialog}
        message="Paciente atualizado com sucesso"
        onClose={() => setShowSuccessDialog(false)}
      />

      {/* Success Dialog for Delete */}
      <SuccessDialog
        isOpen={showDeleteSuccessDialog}
        message="Paciente excluído com sucesso"
        onClose={() => setShowDeleteSuccessDialog(false)}
      />

      {/* Success Dialog for Create */}
      <SuccessDialog
        isOpen={showCreateSuccessDialog}
        message="Paciente incluído com sucesso"
        onClose={() => setShowCreateSuccessDialog(false)}
      />

      {/* Mobile Floating Button - Bottom Right */}
      <button
        onClick={() => setShowAddModal(true)}
        className="lg:hidden fixed bottom-6 right-6 w-14 h-14 bg-blue-600 hover:bg-indigo-700 text-white rounded-full shadow-lg flex items-center justify-center z-40 transition-colors"
        aria-label="Novo Paciente"
      >
        <Plus className="w-6 h-6" />
      </button>
    </div>
  );
}